import { useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Paleta única dos gráficos. Antes cada bloco escrevia o seu hex — havia 18
 * cores diferentes só no Analytics.
 *
 * A regra (ver docs/designsystem/padrao-de-tela.md):
 *   - volume  → a rampa, uma hue só, do menor para o maior
 *   - duas séries de verdade → primary + secondary, nessa ordem, nunca rodízio
 *   - estado  → good/warn/bad, reservados, sempre com rótulo junto
 *
 * ANCORADA NA MARCA (2026-09-10). A hue é a do brikcrm.com.br: todos os azuis
 * do site ficam em H 255–261 (`#00275e` navy, `#001a40` navy escuro, `#2c4e81`
 * médio, `#eaeff6` wash). A rampa clara termina exatamente no navy da marca.
 * O croma foi baixado de 16 para ~11, que é o do site — era isso que fazia o
 * azul antigo (`#2a78d6`) destoar, não a hue.
 *
 * Por que a cor de gráfico NÃO é o `#00275e`/`#0f346b` puro: medido, eles têm
 * L* 28,7 e 33,4 — fora da faixa 0,43–0,77 que a checagem exige, e contraste
 * 1,24 e 1,47 contra a superfície escura (#16171a), ou seja, invisíveis no
 * tema escuro. O navy é cor de CHROME (botão, estado ativo), não de marca de
 * gráfico; para gráfico usamos a mesma hue num passo de luminosidade que
 * sobrevive nas duas superfícies.
 *
 * O par primary/secondary passa nos seis checks da skill `dataviz` nos dois
 * temas (ΔE 24,6 protan no claro, 24,3 deutan no escuro; contraste 7,87 e
 * 5,73 contra a própria superfície).
 */
const PALETTE = {
    light: {
        ramp: ['#dfecff', '#a2c0eb', '#658dc9', '#235193', '#002860'],
        primary: '#235193',
        secondary: '#eb6834',
        good: '#059669',
        warn: '#c2410c',
        bad: '#dc2626',
        track: '#f2f2ef',
        grid: '#f0f0ed',
        axis: '#a3a39a',
        ink: '#1a1a19',
        muted: '#77776f',
        surface: '#ffffff',
        border: '#ececea',
    },
    dark: {
        // Estados e neutros realinhados à rampa central (Direção A, 26/09) —
        // ver docs/designsystem/propostas/modo-escuro.html. Os azuis não mudam.
        ramp: ['#012a63', '#1e4b8d', '#406eb3', '#6293db', '#9cb9e5'],
        primary: '#6293db',
        secondary: '#d95926',
        good: '#3fb27f',
        warn: '#e0a04a',
        bad: '#e5645f',
        track: '#1e2126',
        grid: '#222429',
        axis: '#6b7078',
        ink: '#e3e5ea',
        muted: '#9aa0a8',
        surface: '#16171a',
        border: '#282b31',
    },
} as const;

export type ChartTokens = typeof PALETTE.light;

export const useChartTokens = (): ChartTokens => {
    const { theme } = useTheme();
    return useMemo(() => (theme === 'dark' ? PALETTE.dark : PALETTE.light) as ChartTokens, [theme]);
};

/**
 * Passo da rampa para um valor dentro de uma faixa — usado quando a cor
 * precisa carregar magnitude (barras de crescimento, etapas do funil).
 */
export const rampStep = (tokens: ChartTokens, value: number, max: number): string => {
    if (max <= 0) return tokens.ramp[0];
    const i = Math.min(tokens.ramp.length - 1, Math.max(0, Math.round((value / max) * (tokens.ramp.length - 1))));
    return tokens.ramp[i];
};

/** Estilo do tooltip do recharts, para nenhum gráfico declarar o seu. */
export const useTooltipStyle = () => {
    const t = useChartTokens();
    return useMemo(() => ({
        contentStyle: {
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: '8px',
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.10)',
            padding: '8px 10px',
            fontSize: '13px',
        },
        itemStyle: { color: t.ink, fontSize: '13px', padding: 0 },
        labelStyle: { color: t.muted, fontSize: '12px', marginBottom: '2px' },
        cursor: { fill: 'transparent' } as const,
    }), [t]);
};
